import React, { useState, useEffect } from 'react';
import { API_BASE_URL, axiosInstance, isTokenExpired } from '../config';
import { useNavigate } from 'react-router-dom';
import { useCookies } from 'react-cookie';
import { useGetUserID } from '../hooks/useGetUserID';
import RecipeIngredientInput from '../components/RecipeIngredientInput';
import RecipeImage from '../components/RecipeImage';
import '../App.css';
import '../styles/createrecipe.css';

// Add a key for storing AI recipe drafts
const AI_RECIPE_DRAFT_KEY = 'aiRecipeDraft';

const validateRecipe = (recipe) => {
    console.log('Validating recipe:', recipe); // Debug log

    if (!recipe.name?.trim()) {
        throw new Error('Recipe name is required');
    }
    
    if (!recipe.cookingTime || isNaN(recipe.cookingTime)) {
        throw new Error('Valid cooking time is required');
    }
    
    if (!recipe.servings || isNaN(recipe.servings) || recipe.servings < 1) {
        throw new Error('Valid number of servings is required');
    }
    
    if (!Array.isArray(recipe.ingredients) || recipe.ingredients.length === 0) {
        throw new Error('At least one ingredient is required');
    }
    
    recipe.ingredients.forEach((ing, index) => {
        console.log(`Validating ingredient ${index}:`, ing); // Debug log
        if (!ing?.name?.trim()) {
            throw new Error(`Ingredient name is required at position ${index + 1}`);
        }
        if (!ing?.amount || isNaN(parseFloat(ing.amount))) {
            throw new Error(`Valid ingredient amount is required at position ${index + 1}`);
        }
        if (!ing?.unit?.trim()) {
            throw new Error(`Ingredient unit is required at position ${index + 1}`);
        }
        if (ing.unit === 'other' && !ing?.customUnit?.trim()) {
            throw new Error(`Custom unit required for ingredient at position ${index + 1}`);
        }
    });
    
    if (!Array.isArray(recipe.instructions) || recipe.instructions.length === 0) {
        throw new Error('At least one instruction is required');
    }
    
    recipe.instructions.forEach((instruction, index) => {
        if (typeof instruction === 'string') {
            // Handle old format (string instructions)
            if (!instruction?.trim()) {
                throw new Error(`Instruction ${index + 1} cannot be empty`);
            }
        } else {
            // Handle new format (object instructions)
            if (!instruction?.text?.trim()) {
                throw new Error(`Instruction ${index + 1} text cannot be empty`);
            }
        }
    });

    console.log('Recipe validation passed'); // Debug log
};

const DEFAULT_IMAGE_URL = 'https://via.placeholder.com/400x300?text=No+Image+Available';

// Food puns for loading screen
const FOOD_PUNS = [
  "Cooking up something special... Just for you!",
  "Whisking away to find your perfect recipe...",
  "This is going to be a recipe for success!",
  "Stirring up the perfect ingredients...",
  "Adding a pinch of AI magic to your dish...",
  "We're on a roll with your recipe...",
  "Creating a recipe that's sure to curry favor!",
  "Lettuce show you what we can cook up...",
  "This recipe is going to be grate!",
  "We're not bacon this up, your recipe is coming soon!",
  "Your recipe will be worth the thyme...",
  "You're going to loaf this recipe!",
  "Hope you're not feeling too hungry, we're cooking as fast as we can!",
  "Don't go bacon my heart, your recipe is almost ready...",
  "We're having a ball creating your recipe!",
  "This recipe will be the apple of your eye...",
  "Your recipe is simmering to perfection...",
  "We donut want to keep you waiting much longer...",
  "Your recipe is going to be soup-er!",
  "Just a little more thyme needed..."
];

export default function CreateRecipe() {
    const [cookies] = useCookies(["access_token"]);
    const [recipe, setRecipe] = useState({
        name: '',
        instructions: [{ text: '', imageUrl: '' }],
        imageUrl: '',
        ingredientsPhotoUrl: '',
        cookingTime: '',
        servings: 1,
        selection: 'quick and easy', // Add required selection field
        ingredients: [{ name: '', amount: '', unit: 'g', customUnit: '', displayText: '', preparation: '' }],
        isVegan: false,
        isVegetarian: false,
        categories: []
    });
    const [error, setError] = useState('');
    const [isGenerating, setIsGenerating] = useState(false);
    const [isUsingAI, setIsUsingAI] = useState(false);
    const [recipePrompt, setRecipePrompt] = useState('');
    const [servingSize, setServingSize] = useState(4);
    const [ingredientComplexity, setIngredientComplexity] = useState('normal');
    const [preference, setPreference] = useState('cost_effective');
    const [generatedImageUrl, setGeneratedImageUrl] = useState('');
    const [generateImage, setGenerateImage] = useState(true);
    const [selectedIngredientCategories, setSelectedIngredientCategories] = useState([]);
    const [aiDietary, setAiDietary] = useState({ isVegan: false, isVegetarian: false });
    const [aiCategories, setAiCategories] = useState([]);
    
    // New state for loading screen
    const [currentPun, setCurrentPun] = useState('');
    const [recipeGenerated, setRecipeGenerated] = useState(false);
    
    // Cupboard integration state
    const [cupboardItems, setCupboardItems] = useState([]);
    const [useCupboardMode, setUseCupboardMode] = useState(false);
    const [selectedCupboardItems, setSelectedCupboardItems] = useState([]);
    const [cupboardMealType, setCupboardMealType] = useState('');
    
    // New state for web search results
    const [webSearchResults, setWebSearchResults] = useState('');
    const [showWebSearchDetails, setShowWebSearchDetails] = useState(false);
    
    // New state for recipe sources
    const [recipeSources, setRecipeSources] = useState([]);
    const [showSourcesDetails, setShowSourcesDetails] = useState(false);
    
    // New state for token validation with no initial delay
    const [tokenValidated, setTokenValidated] = useState(false);
    const [tokenValidationAttempted, setTokenValidationAttempted] = useState(false);
    
    // Add state for AI recipe recovery
    const [hasRecoveredRecipe, setHasRecoveredRecipe] = useState(false);
    
    // Add state to track if the current recipe was originally AI-generated
    const [wasOriginallyAIGenerated, setWasOriginallyAIGenerated] = useState(false);
    
    // Add new state for tracking the AI process
    const [aiProcessDetails, setAiProcessDetails] = useState(null);
    const [showProcessDetails, setShowProcessDetails] = useState(false);
    const [currentProcessStep, setCurrentProcessStep] = useState('');
    
    // AI Quota state
    const [aiQuota, setAiQuota] = useState(null);
    const [quotaLoading, setQuotaLoading] = useState(false);
    
    // AI Image Quota state
    const [aiImageQuota, setAiImageQuota] = useState(null);
    const [imageQuotaLoading, setImageQuotaLoading] = useState(false);
    
    // Photo generation state for manual recipes
    const [isGeneratingPhotos, setIsGeneratingPhotos] = useState(false);
    const [photoGenerationError, setPhotoGenerationError] = useState('');
    const [photoGenerationSuccess, setPhotoGenerationSuccess] = useState('');
    const [photoGenerationAvailable, setPhotoGenerationAvailable] = useState(false);
    const [showPhotoGenerationOption, setShowPhotoGenerationOption] = useState(false);
    const [lastCreatedRecipeId, setLastCreatedRecipeId] = useState(null);
    
    const navigate = useNavigate();
    const userID = useGetUserID();
    const [selectedImage, setSelectedImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    // State for instruction images
    const [instructionImages, setInstructionImages] = useState({});
    const [instructionImagePreviews, setInstructionImagePreviews] = useState({});
    
    // State for ingredients photo
    const [selectedIngredientsPhoto, setSelectedIngredientsPhoto] = useState(null);
    const [ingredientsPhotoPreview, setIngredientsPhotoPreview] = useState(null);


    // Add available categories constant - South African themed
    const AVAILABLE_CATEGORIES = [
        'flavoured chicken',
        'break a egg breakfasts',
        'cape malay indian',
        'south african',
        'baked goods',
        'braai & side dishes',
        'vegetarian',
        'decadently healthy desserts',
        'healthy classics'
    ];

    // Map category values to display names
    const categoryDisplayNames = {
        'flavoured chicken': 'Flavoursome Chicken',
        'break a egg breakfasts': 'Break-a-Egg Breakfasts',
        'cape malay indian': 'Cape Malay & Indian Spice',
        'south african': 'Proudly South African',
        'baked goods': 'Oven-Fresh Baked Goods',
        'braai & side dishes': 'Braai & Sides',
        'vegetarian': 'Garden Fresh Veggie',
        'decadently healthy desserts': 'Decadently Healthy Desserts',
        'healthy classics': 'Healthy Classics'
    };

    // Fetch AI quota status
    const fetchAiQuota = async () => {
        try {
            setQuotaLoading(true);
            const response = await axiosInstance.get('/recipes/ai-quota');
            if (response.data.success) {
                setAiQuota(response.data.quota);
            }
        } catch (error) {
            console.error('Error fetching AI quota:', error);
            // Don't show error to user, just log it
        } finally {
            setQuotaLoading(false);
        }
    };

    // Fetch AI image quota status
    const fetchAiImageQuota = async () => {
        try {
            setImageQuotaLoading(true);
            const response = await axiosInstance.get('/recipes/ai-image-quota');
            if (response.data.success) {
                setAiImageQuota(response.data.quota);
            }
        } catch (error) {
            console.error('Error fetching AI image quota:', error);
            // Don't show error to user, just log it
        } finally {
            setImageQuotaLoading(false);
        }
    };

    // Add ingredient categories constant
    const INGREDIENT_CATEGORIES = {
        mains: {
            title: 'Main Dish Type',
            options: ['meat', 'fish', 'pizza', 'dish']
        },
        sides: {
            title: 'Side Options',
            options: ['salad', 'veg'] 
        },
        enhancers: {
            title: 'Flavor Enhancers',
            options: ['sauce', 'powder']
        },
        desserts: {
            title: 'Dessert',
            options: ['bake']
        }
    };

    // Update state to store one selection per category group
    const [categorySelections, setCategorySelections] = useState({
        mains: '',
        sides: '',
        enhancers: '',
        desserts: ''
    });

    useEffect(() => {
        // Check for post-login redirect
        const postLoginRedirect = sessionStorage.getItem('postLoginRedirect');
        if (postLoginRedirect) {
            sessionStorage.removeItem('postLoginRedirect');
            console.log('Post-login redirect detected, navigating to:', postLoginRedirect);
            navigate(postLoginRedirect, { replace: true });
            return;
        }

    }, [navigate]);

    // Separate effect for cupboard fetching that depends on authentication
    useEffect(() => {
        // Check for authentication from either source
        const cookieToken = cookies.access_token;
        const localStorageToken = localStorage.getItem('auth_token');
        const hasToken = !!cookieToken || !!localStorageToken;
        
        if (userID && hasToken) {
            console.log('Fetching cupboard items - authenticated user detected');
            fetchCupboardItems();
            fetchAiQuota(); // Also fetch AI quota when authenticated
            fetchAiImageQuota(); // Fetch AI image quota when authenticated
            checkPhotoGenerationAvailability(); // Check photo generation availability
        } else {
            console.log('Skipping cupboard fetch - no authentication or userID');
        }
    }, [userID, cookies.access_token, tokenValidated]);

    // Fetch cupboard items
    const fetchCupboardItems = async () => {
        try {
            if (!userID || !cookies.access_token) return;
            
            const response = await axiosInstance.get(`/cupboard/${userID}`);
            
            if (response.data.success) {
                setCupboardItems(response.data.items);
            }
        } catch (error) {
            console.error('Error fetching cupboard items:', error);
        }
    };

    // Check photo generation availability
    const checkPhotoGenerationAvailability = async () => {
        try {
            const response = await axiosInstance.get('/recipes/photo-generation/status');
            setPhotoGenerationAvailable(response.data.available);
        } catch (error) {
            console.error('Error checking photo generation availability:', error);
            setPhotoGenerationAvailable(false);
        }
    };

    // Add state for progress tracking in CreateRecipe
    const [createRecipePhotoProgress, setCreateRecipePhotoProgress] = useState('');
    const [createRecipePhotosCompleted, setCreateRecipePhotosCompleted] = useState(0);
    const [createRecipeTotalPhotos, setCreateRecipeTotalPhotos] = useState(0);

    // Photo generation function using reliable polling system (no timeouts!)
    const handleGeneratePhotosForRecipe = async (recipeId) => {
        try {
            setIsGeneratingPhotos(true);
            setPhotoGenerationError('');
            setPhotoGenerationSuccess('');
            setCreateRecipePhotoProgress('Starting photo generation...');
            setCreateRecipePhotosCompleted(0);
            setCreateRecipeTotalPhotos(6); // Estimate: 1 ingredient + 5 step photos

            console.log('Starting photo generation for recipe:', recipeId);

            // Step 1: Start the photo generation job
            setCreateRecipePhotoProgress('Initializing photo generation job...');

            const response = await axiosInstance.post(
                `/recipes/${recipeId}/generate-photos`,
                {
                    userID: userID,
                    generateIngredientPhoto: true,
                    generateStepPhotos: true,
                    maxStepPhotos: 5,
                    useCache: true
                }
            );

            if (!response.data.jobId) {
                throw new Error('No job ID returned from the server');
            }

            const jobId = response.data.jobId;
            console.log('Photo generation job created:', jobId);

            // Step 2: Poll for job completion
            let photoData = null;
            let attempts = 0;
            const maxAttempts = 120; // 10 minutes timeout (120 * 5 seconds = 600 seconds = 10 minutes)
            
            while (!photoData && attempts < maxAttempts) {
                attempts++;
                
                try {
                    // Wait 5 seconds between polls
                    await new Promise(resolve => setTimeout(resolve, 5000));
                    
                    const statusResponse = await axiosInstance.get(`/recipes/photo-job-status/${jobId}`);
                    
                    // Update progress display
                    if (statusResponse.data.message) {
                        setCreateRecipePhotoProgress(statusResponse.data.message);
                    }
                    
                    if (statusResponse.data.progress) {
                        setCreateRecipePhotosCompleted(Math.floor((statusResponse.data.progress / 100) * 6));
                    }
                    
                    if (statusResponse.data.status === 'completed') {
                        photoData = statusResponse.data;
                        break;
                    } else if (statusResponse.data.status === 'failed') {
                        throw new Error(statusResponse.data.error || 'Photo generation failed');
                    }
                    
                    console.log(`Photo generation progress: ${statusResponse.data.message} (${statusResponse.data.progress}%)`);
                    
                } catch (pollError) {
                    console.error('Error polling photo generation status:', pollError);
                    if (attempts >= maxAttempts) {
                        throw new Error('Photo generation timed out after 10 minutes. Please try again later.');
                    }
                }
            }
            
            if (!photoData) {
                throw new Error('Photo generation timed out after 10 minutes. Please try again with a simpler recipe.');
            }
            
            console.log('Photo generation completed:', photoData);
            
            // Step 3: Handle successful completion
            if (photoData.result && photoData.result.photoResults) {
                const photoResults = photoData.result.photoResults;
                
                setCreateRecipePhotosCompleted(photoResults.totalPhotosGenerated);
                setCreateRecipePhotoProgress('Photos successfully created and saved!');

                setPhotoGenerationSuccess(
                    `Successfully generated ${photoResults.totalPhotosGenerated} photos! ` +
                    `${photoResults.ingredientPhotoGenerated ? 'Ingredient overview, ' : ''}` +
                    `${photoResults.stepPhotosGenerated} step photos created.`
                );

                console.log('Photo generation completed successfully:', photoResults);
                
                // Automatically navigate to recipe after success
                setTimeout(() => {
                    setShowPhotoGenerationOption(false);
                    navigate(`/recipe/${recipeId}`);
                }, 2000);
            } else {
                throw new Error('Photo generation completed but no results received');
            }

        } catch (error) {
            console.error('Error generating photos:', error);
            
            let errorMessage = error.response?.data?.message || error.message || 'Failed to generate photos';
            
            // Handle specific error types
            if (error.response?.status === 503) {
                errorMessage = 'Photo generation service is currently unavailable. Please try again later.';
            } else if (error.response?.status === 403) {
                errorMessage = 'You can only generate photos for your own recipes.';
            } else if (error.response?.status === 429) {
                // Handle quota limits
                const quotaError = error.response.data;
                if (quotaError.error === 'MONTHLY_IMAGE_LIMIT_EXCEEDED') {
                    const timeMessage = quotaError.timeUntilResetText 
                        ? ` You can generate AI photos again in ${quotaError.timeUntilResetText}.`
                        : ' Try again next month.';
                    errorMessage = `Monthly AI image limit reached (${quotaError.monthlyLimit} per month).${timeMessage}`;
                }
            }
            
            setPhotoGenerationError(errorMessage);
            setCreateRecipePhotoProgress('');
        } finally {
            setIsGeneratingPhotos(false);
        }
    };

    // Add pun rotation effect
    useEffect(() => {
        if (!isGenerating) return;
        
        // Set initial pun
        setCurrentPun(FOOD_PUNS[Math.floor(Math.random() * FOOD_PUNS.length)]);
        
        // Rotate puns every 5 seconds
        const punInterval = setInterval(() => {
            const randomIndex = Math.floor(Math.random() * FOOD_PUNS.length);
            setCurrentPun(FOOD_PUNS[randomIndex]);
        }, 5000);
        
        return () => clearInterval(punInterval);
    }, [isGenerating]);

    // Check for saved recipe on initial load
    useEffect(() => {
        const savedRecipe = localStorage.getItem(AI_RECIPE_DRAFT_KEY);
        if (savedRecipe && !hasRecoveredRecipe) {
            try {
                const parsedRecipe = JSON.parse(savedRecipe);
                // Only show recovery option if there's actual content
                if (parsedRecipe.name || 
                    (parsedRecipe.ingredients && parsedRecipe.ingredients.length > 0) || 
                    (parsedRecipe.instructions && parsedRecipe.instructions.length > 0)) {
                    setHasRecoveredRecipe(true);
                }
            } catch (e) {
                console.error('Error parsing saved recipe:', e);
                localStorage.removeItem(AI_RECIPE_DRAFT_KEY);
            }
        }
    }, [hasRecoveredRecipe]);

    // Simplified token validation - check both sources immediately
    useEffect(() => {
        const validateToken = () => {
            // Check both cookie and localStorage for a token
            const cookieToken = cookies.access_token;
            const localStorageToken = localStorage.getItem('auth_token');
            const hasToken = !!cookieToken || !!localStorageToken;

            console.log('Token validation check:', {
                inCookie: !!cookieToken,
                inLocalStorage: !!localStorageToken,
                hasToken
            });

            if (!hasToken) {
                console.log('No auth token available');
                setTokenValidated(false);
                setTokenValidationAttempted(true);
                return;
            }

            // Get the token to validate (prefer cookie, fallback to localStorage)
            const token = cookieToken || localStorageToken;
            
            try {
                // Manually validate the token by decoding and checking expiration
                const parts = token.split('.');
                if (parts.length !== 3) {
                    throw new Error('Invalid token format');
                }
                
                // Decode the payload
                const payload = JSON.parse(atob(parts[1]));
                const now = Math.floor(Date.now() / 1000);
                
                // Check if token is expired
                if (payload.exp && payload.exp < now) {
                    console.log('Token has expired');
                    setTokenValidated(false);
                    setTokenValidationAttempted(true);
                    setError('Your session has expired. Please log in again.');
                    return;
                }
                
                console.log('Token validated successfully');
                setTokenValidated(true);
                setTokenValidationAttempted(true);
                setError(''); // Clear any previous session errors
                
            } catch (tokenError) {
                console.error('Token validation failed:', tokenError);
                setTokenValidated(false);
                setTokenValidationAttempted(true);
                setError('Authentication error. Please log in again.');
            }
        };

        // Run validation immediately
        validateToken();
        
    }, [cookies.access_token]);

    // Function to recover saved recipe
    const recoverSavedRecipe = () => {
        try {
            const savedRecipe = localStorage.getItem(AI_RECIPE_DRAFT_KEY);
            if (savedRecipe) {
                const parsedRecipe = JSON.parse(savedRecipe);
                setRecipe(parsedRecipe);
                
                // Set other states based on the saved recipe
                setIsUsingAI(true);
                setRecipeGenerated(true);
                
                // Mark that this was originally AI-generated since it was saved from AI generation
                setWasOriginallyAIGenerated(true);
                
                // If there's an image URL, set it
                if (parsedRecipe.imageUrl) {
                    setGeneratedImageUrl(parsedRecipe.imageUrl);
                    setImagePreview(parsedRecipe.imageUrl);
                }
                
                // Clear the recovery flag
                setHasRecoveredRecipe(false);
                
                // Clear saved draft after successful recovery
                localStorage.removeItem(AI_RECIPE_DRAFT_KEY);
            }
        } catch (e) {
            console.error('Error recovering saved recipe:', e);
            localStorage.removeItem(AI_RECIPE_DRAFT_KEY);
            setHasRecoveredRecipe(false);
        }
    };

    // Toggle between manual creation and AI generation modes
    const toggleAIMode = () => {
        // Toggle the AI mode
        const newIsUsingAI = !isUsingAI;
        setIsUsingAI(newIsUsingAI);
        
        if (newIsUsingAI) {
            // Switching to AI mode, reset recipe and prepare for AI generation
            setRecipe({
                name: "",
                ingredients: [],
                instructions: [{ text: '', imageUrl: '' }],
                cookingTime: "30",
                servings: "4",
                selection: "quick and easy", // Add required selection field
                imageUrl: "",
                userOwner: userID,
                isVegan: false,
                isVegetarian: false,
                categories: []
            });
            setRecipePrompt('');
            setGeneratedImageUrl('');
            // Reset category selections
            setCategorySelections({
                mains: '',
                sides: '',
                enhancers: '',
                desserts: ''
            });
            // Reset cupboard mode
            setUseCupboardMode(false);
            setSelectedCupboardItems([]);
            setCupboardMealType('');
            // Reset the AI generation flag since we're starting fresh
            setWasOriginallyAIGenerated(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (type === 'checkbox') {
            setRecipe({ ...recipe, [name]: checked });
        } else {
            setRecipe({ ...recipe, [name]: value });
        }
    };

    const handleIngredientChange = (index, field, value) => {
        const updatedIngredients = [...recipe.ingredients];
        updatedIngredients[index] = {
            ...updatedIngredients[index],
            [field]: value
        };
        setRecipe({ ...recipe, ingredients: updatedIngredients });
    };

    const handleAddIngredient = () => {
        setRecipe({
            ...recipe,
            ingredients: [...recipe.ingredients, { name: '', amount: '', unit: 'g', customUnit: '', displayText: '', preparation: '' }]
        });
    };

    const handleRemoveIngredient = (index) => {
        const updatedIngredients = recipe.ingredients.filter((_, i) => i !== index);
        setRecipe({ ...recipe, ingredients: updatedIngredients });
    };

    const handleInstructionChange = (index, field, value) => {
        const newInstructions = [...recipe.instructions];
        newInstructions[index] = {
            ...newInstructions[index],
            [field]: value
        };
        setRecipe({ ...recipe, instructions: newInstructions });
    };

    const addInstruction = () => {
        setRecipe({
            ...recipe,
            instructions: [...recipe.instructions, { text: '', imageUrl: '' }]
        });
    };

    const removeInstruction = (index) => {
        const newInstructions = recipe.instructions.filter((_, i) => i !== index);
        setRecipe({ ...recipe, instructions: newInstructions });
        
        // Clean up associated image states
        const newInstructionImages = { ...instructionImages };
        const newInstructionImagePreviews = { ...instructionImagePreviews };
        delete newInstructionImages[index];
        delete newInstructionImagePreviews[index];
        
        // Reindex remaining images
        const reindexedImages = {};
        const reindexedPreviews = {};
        Object.keys(newInstructionImages).forEach(key => {
            const oldIndex = parseInt(key);
            const newIndex = oldIndex > index ? oldIndex - 1 : oldIndex;
            reindexedImages[newIndex] = newInstructionImages[key];
        });
        Object.keys(newInstructionImagePreviews).forEach(key => {
            const oldIndex = parseInt(key);
            const newIndex = oldIndex > index ? oldIndex - 1 : oldIndex;
            reindexedPreviews[newIndex] = newInstructionImagePreviews[key];
        });
        
        setInstructionImages(reindexedImages);
        setInstructionImagePreviews(reindexedPreviews);
    };

    const handleImageChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setSelectedImage(file);

        // Show preview
        const reader = new FileReader();
        reader.onloadend = () => {
            setImagePreview(reader.result);
        };
        reader.onerror = () => {
            console.error('Error reading file');
            setImagePreview(DEFAULT_IMAGE_URL);
        };
        reader.readAsDataURL(file);
    };

    const handleInstructionImageChange = async (index, e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Update instruction images state
        setInstructionImages(prev => ({
            ...prev,
            [index]: file
        }));

        // Show preview
        const reader = new FileReader();
        reader.onloadend = () => {
            setInstructionImagePreviews(prev => ({
                ...prev,
                [index]: reader.result
            }));
        };
        reader.onerror = () => {
            console.error('Error reading instruction image file');
        };
        reader.readAsDataURL(file);
    };

    const removeInstructionImage = (index) => {
        setInstructionImages(prev => {
            const newImages = { ...prev };
            delete newImages[index];
            return newImages;
        });

        setInstructionImagePreviews(prev => {
            const newPreviews = { ...prev };
            delete newPreviews[index];
            return newPreviews;
        });

        // Clear the imageUrl from recipe instructions
        handleInstructionChange(index, 'imageUrl', '');
    };

    const handleIngredientsPhotoChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validate file type
        if (!file.type.startsWith('image/')) {
            setError('Please select an image file');
            return;
        }

        // Validate file size (5MB limit)
        const maxSize = 5 * 1024 * 1024;
        if (file.size > maxSize) {
            setError('File size too large. Please select an image under 5MB');
            return;
        }

        setSelectedIngredientsPhoto(file);
        
        // Show preview
        const reader = new FileReader();
        reader.onloadend = () => {
            setIngredientsPhotoPreview(reader.result);
        };
        reader.readAsDataURL(file);
    };

    const removeIngredientsPhoto = () => {
        setSelectedIngredientsPhoto(null);
        setIngredientsPhotoPreview(null);
    };

    const handleCategoryChange = (category) => {
        setRecipe(prev => ({
            ...prev,
            categories: prev.categories.includes(category)
                ? prev.categories.filter(c => c !== category)
                : [...prev.categories, category]
        }));
    };

    // Handle ingredient category selection
    const handleIngredientCategoryChange = (category) => {
        setSelectedIngredientCategories(prev => 
            prev.includes(category)
                ? prev.filter(c => c !== category)
                : [...prev, category]
        );
    };

    // Handle AI category change
    const handleAiCategoryChange = (category) => {
        setAiCategories(prev => 
            prev.includes(category) 
                ? prev.filter(c => c !== category) 
                : [...prev, category]
        );
    };

    // Handle AI dietary preference change
    const handleAiDietaryChange = (preference) => {
        setAiDietary(prev => ({
            ...prev, 
            [preference]: !prev[preference]
        }));
    };

    // Update the handler for category selection
    const handleCategoryGroupSelection = (group, option) => {
        setCategorySelections(prev => ({
            ...prev,
            [group]: prev[group] === option ? '' : option
        }));
    };

    // AI Recipe Generation Handler
    const handleGenerateRecipe = async (e) => {
        e.preventDefault();
        
        // Handle cupboard mode differently
        let finalPrompt = recipePrompt;
        
        if (useCupboardMode) {
            if (selectedCupboardItems.length === 0) {
                setError('Please select at least one ingredient from your cupboard.');
                return;
            }
            
            // Create prompt from selected ingredients and meal type
            const selectedIngredients = cupboardItems
                .filter(item => selectedCupboardItems.includes(item._id))
                .map(item => item.ingredientName);
            
            const ingredientText = selectedIngredients.join(', ');
            const mealTypeText = cupboardMealType ? ` for a ${cupboardMealType}` : ' and create something nice and warm';
            
            finalPrompt = `I have ${ingredientText} in my cupboard. Please search the web for nice recipes that use these ingredients${mealTypeText}.`;
            setRecipePrompt(finalPrompt);
        } else {
            if (!recipePrompt.trim()) {
                setError('Please enter a description for the recipe you want to generate.');
                return;
            }
        }
        
        // Ensure we have a valid prompt before proceeding
        if (!finalPrompt || finalPrompt.trim().length === 0) {
            setError('Unable to generate recipe prompt. Please try again.');
            return;
        }
        
        setError('');
        setIsGenerating(true);
        setRecipeGenerated(false);
        setAiProcessDetails(null); // Reset process details
        
        // Enhanced authentication check that checks both cookie and localStorage
        const cookieToken = cookies.access_token;
        const localStorageToken = localStorage.getItem('auth_token');
        const hasToken = !!cookieToken || !!localStorageToken;
        
        if (!hasToken) {
            console.error('No authentication token found');
            setError('You need to be logged in to generate recipes. Please log in and try again.');
            setIsGenerating(false);
            return;
        }
        
        // Don't require tokenValidated to be true - just check if we have a token
        console.log('Auth token present:', {
            tokenValidated,
            inCookie: !!cookieToken,
            inLocalStorage: !!localStorageToken,
            userID: !!userID
        });
        
        try {
            // Get the selected categories (non-empty values only)
            const selectedCategories = Object.values(categorySelections).filter(Boolean);
            
            // Get cupboard ingredients if in cupboard mode or prompt mentions cupboard
            let cupboardIngredientsForAI = [];
            if (useCupboardMode && selectedCupboardItems.length > 0) {
                cupboardIngredientsForAI = cupboardItems
                    .filter(item => selectedCupboardItems.includes(item._id))
                    .map(item => ({
                        name: item.ingredientName,
                        amount: item.amount,
                        unit: item.unit,
                        location: item.location
                    }));
            } else if (recipePrompt.toLowerCase().includes('cupboard')) {
                cupboardIngredientsForAI = cupboardItems.map(item => ({
                    name: item.ingredientName,
                    amount: item.amount,
                    unit: item.unit,
                    location: item.location
                }));
            }
            
            console.log('Generating recipe with categories:', selectedCategories);
            console.log('AI dietary preferences:', aiDietary);
            console.log('AI recipe categories:', aiCategories);
            console.log('Ingredient complexity:', ingredientComplexity);
            console.log('Cupboard ingredients for AI:', cupboardIngredientsForAI);
            console.log('Final prompt being sent:', finalPrompt);
            console.log('🖼️ Image generation settings:', {
                generateImage,
                generateImageType: typeof generateImage,
                checkboxChecked: generateImage === true
            });
            
            // Use axiosInstance instead of axios to ensure token handling and interceptors are used
            const response = await axiosInstance.post(
                `/recipes/generate`,
                { 
                    prompt: finalPrompt,
                    preference: preference,
                    servingSize: servingSize,
                    ingredientComplexity: ingredientComplexity,
                    ingredientCategories: selectedCategories,
                    recipeCategories: aiCategories,
                    dietaryPreferences: aiDietary,
                    generateImage: generateImage,
                    cupboardIngredients: cupboardIngredientsForAI
                }
            );
            
            // The endpoint now returns a jobId that we need to poll
            if (!response.data.jobId) {
                throw new Error('No job ID returned from the server');
            }
            
            const jobId = response.data.jobId;
            console.log('Recipe generation job created:', jobId);
            
            // Step 2: Poll for the job results
            // gpt-5 is a reasoning model and can take 2-3 minutes per call (2 calls = up to 6 minutes)
            let recipeData = null;
            let attempts = 0;
            const maxAttempts = 120; // 10 minutes (120 * 5 seconds)
            
            while (!recipeData && attempts < maxAttempts) {
                attempts++;
                
                // First poll after 3s so user sees initial progress quickly; then every 5s
                const delayMs = attempts === 1 ? 3000 : 5000;
                await new Promise(resolve => setTimeout(resolve, delayMs));
                
                let statusResponse;
                try {
                    statusResponse = await axiosInstance.get(`/recipes/job-status/${jobId}`);
                } catch (networkError) {
                    // 404 means the job was lost (server restarted) - stop immediately
                    if (networkError.response?.status === 404) {
                        throw new Error('Recipe generation job was lost (server may have restarted). Please try again.');
                    }
                    // Other network errors - log and retry
                    console.warn('Network error polling job status, will retry:', networkError.message);
                    continue;
                }
                
                // Update current process step for display
                if (statusResponse.data.details) {
                    setCurrentProcessStep(statusResponse.data.message);
                    if (statusResponse.data.details.step) {
                        setAiProcessDetails(prev => ({
                            ...prev,
                            [statusResponse.data.details.step]: statusResponse.data.details
                        }));
                    }
                }
                
                const jobStatus = statusResponse.data.status;
                
                if (jobStatus === 'completed') {
                    recipeData = statusResponse.data;
                    break;
                } else if (jobStatus === 'failed') {
                    // Job explicitly failed - stop polling and show the error immediately
                    throw new Error(statusResponse.data.error || 'Recipe generation failed. Please try again.');
                }
                
                // Still processing - log progress
                console.log(`AI Process: ${statusResponse.data.message} (${statusResponse.data.progress}%)`);
            }
            
            if (!recipeData) {
                throw new Error('Recipe generation timed out after 10 minutes. Please try again.');
            }
            
            console.log('Generated recipe response:', recipeData);
            console.log('Recipe data structure check:', {
                hasResult: !!recipeData.result,
                hasRecipe: !!(recipeData.result && recipeData.result.recipe),
                resultKeys: recipeData.result ? Object.keys(recipeData.result) : [],
                status: recipeData.status
            });
            
            // Update the form with the generated recipe data
            // The recipe data is inside result.recipe from the job status response
            if (recipeData.result && recipeData.result.recipe) {
                const generatedRecipe = recipeData.result.recipe;
                
                // Store web search results for display
                if (recipeData.result.recipeKnowledge) {
                    setWebSearchResults(recipeData.result.recipeKnowledge);
                    console.log('Recipe knowledge received:', recipeData.result.recipeKnowledge.substring(0, 200) + '...');
                }
                
                // Store recipe sources for display
                if (recipeData.result.sources) {
                    setRecipeSources(recipeData.result.sources);
                    console.log('Recipe sources found:', recipeData.result.sources);
                } else {
                    setRecipeSources([]);
                }
                
                // Store instructional roadmap for display
                if (recipeData.result.instructionalRoadmap) {
                    console.log('Instructional roadmap received:', recipeData.result.instructionalRoadmap);
                    // The roadmap will be included in the recipe data automatically
                }
                
                // Log instruction images information
                if (recipeData.result.instructionImages) {
                    console.log('Instruction images received:', recipeData.result.instructionImages);
                    console.log('Number of instruction images:', recipeData.result.instructionImagesCount || 0);
                }
                console.log('Processing generated recipe:', {
                    name: generatedRecipe.name,
                    ingredientCount: generatedRecipe.ingredients?.length,
                    instructionCount: generatedRecipe.instructions?.length,
                    complexity: ingredientComplexity
                });
                
                // Handle image generation results
                const imageUrl = recipeData.result.imageUrl || generatedRecipe.imageUrl || null;
                const ingredientsImageUrl = recipeData.result.ingredientsImageUrl || null;
                const imageError = recipeData.result.imageGenerationError || null;
                const imageRequested = recipeData.result.imageGenerationRequested || false;
                
                console.log('Image generation results:', {
                    imageUrl: imageUrl ? imageUrl.substring(0, 60) + '...' : null,
                    ingredientsImageUrl: ingredientsImageUrl ? ingredientsImageUrl.substring(0, 60) + '...' : null,
                    imageError,
                    imageRequested,
                    hasValidImage: !!(imageUrl && imageUrl !== 'null' && !imageUrl.includes('placeholder')),
                    hasValidIngredientsImage: !!(ingredientsImageUrl && ingredientsImageUrl !== 'null' && !ingredientsImageUrl.includes('placeholder'))
                });
                
                if (imageUrl && imageUrl !== 'null' && !imageUrl.includes('placeholder')) {
                    console.log('✅ Setting generated image URL:', imageUrl.substring(0, 60) + '...');
                    setGeneratedImageUrl(imageUrl);
                    setImagePreview(imageUrl);
                } else {
                    console.log('❌ No valid AI image generated');
                    setGeneratedImageUrl('');
                    setImagePreview(null);
                    
                    // Show error message if image was requested but failed
                    if (imageRequested && imageError) {
                        console.error('Image generation error:', imageError);
                        setError(`Image generation failed: ${imageError}`);
                    }
                }
                
                // Merge AI-generated dietary preferences with user selections
                // If user selected specific dietary preferences, use those, otherwise use what the model detected
                const mergedDietary = {
                    isVegan: aiDietary.isVegan || (generatedRecipe.isVegan || false),
                    isVegetarian: aiDietary.isVegetarian || (generatedRecipe.isVegetarian || false)
                };
                
                // If user selected specific categories, use those, otherwise use what the model detected
                // If neither user nor model selected categories, use an empty array
                const mergedCategories = aiCategories.length > 0 
                    ? aiCategories
                    : (generatedRecipe.categories || []);
                
                // Convert AI instructions (array of strings) to new format (array of objects)
                const convertedInstructions = generatedRecipe.instructions.map(instruction => ({
                    text: typeof instruction === 'string' ? instruction : instruction.text || '',
                    imageUrl: typeof instruction === 'object' ? instruction.imageUrl || '' : ''
                }));

                // Create the updated recipe object
                const updatedRecipe = {
                    ...recipe,
                    name: generatedRecipe.name || generatedRecipe.title, // Support both field names
                    ingredients: generatedRecipe.ingredients,
                    instructions: convertedInstructions,
                    isVegan: mergedDietary.isVegan,
                    isVegetarian: mergedDietary.isVegetarian,
                    categories: mergedCategories,
                    imageUrl: imageUrl || '', // Use generated image or empty string
                    ingredientsPhotoUrl: ingredientsImageUrl || '', // Use AI-generated ingredients overview image
                    cookingTime: generatedRecipe.cookingTime || 30,
                    servings: servingSize
                };
                
                // Update recipe state
                setRecipe(updatedRecipe);
                
                // Save the generated recipe to localStorage
                localStorage.setItem(AI_RECIPE_DRAFT_KEY, JSON.stringify(updatedRecipe));
                
                console.log('Updated recipe state with generated data:', {
                    name: generatedRecipe.name || generatedRecipe.title,
                    ingredients: generatedRecipe.ingredients.length,
                    cookingTime: generatedRecipe.cookingTime || 30,
                    servings: servingSize,
                    isVegan: mergedDietary.isVegan,
                    isVegetarian: mergedDietary.isVegetarian,
                    categories: mergedCategories,
                    imageUrl: imageUrl ? 'Set' : 'Not set',
                    ingredientsPhotoUrl: ingredientsImageUrl ? 'Set' : 'Not set'
                });

                // Set recipe as generated
                setRecipeGenerated(true);
                
                // Mark that this recipe was originally AI-generated
                setWasOriginallyAIGenerated(true);
                
                // Refresh AI quota and image quota after successful generation
                fetchAiQuota();
                fetchAiImageQuota();
                
                // Scroll to recipe preview
                setTimeout(() => {
                    const previewElement = document.querySelector('.generated-recipe-preview');
                    if (previewElement) {
                        previewElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                }, 500);
            }
            
            // Store the complete processing details
            if (recipeData.result && recipeData.result.processingDetails) {
                setAiProcessDetails(recipeData.result.processingDetails);
            }
            
        } catch (error) {
            console.error('Error generating recipe:', error);
            
            if (error.response && error.response.status === 401) {
                // Handle authentication error specifically
                setError('Authentication error. Please log out and log back in to continue.');
            } else if (error.response && error.response.status === 429) {
                // Handle quota exceeded error
                const quotaError = error.response.data;
                if (quotaError.error === 'DAILY_LIMIT_EXCEEDED') {
                    const timeMessage = quotaError.timeUntilResetText 
                        ? ` You can create another recipe in ${quotaError.timeUntilResetText}.`
                        : ' Try again tomorrow.';
                    setError(`Daily AI recipe limit reached (${quotaError.dailyLimit} per day).${timeMessage}`);
                    // Refresh quota to show updated status
                    fetchAiQuota();
                } else if (quotaError.error === 'MONTHLY_IMAGE_LIMIT_EXCEEDED') {
                    const timeMessage = quotaError.timeUntilResetText 
                        ? ` You can create AI recipes with images again in ${quotaError.timeUntilResetText}.`
                        : ' Try again next month.';
                    setError(`Monthly AI image limit reached (${quotaError.monthlyLimit} per month).${timeMessage} You can still create AI recipes without images (2 per day) or manual recipes (unlimited).`);
                    // Refresh both quotas to show updated status
                    fetchAiQuota();
                    fetchAiImageQuota();
                } else {
                    setError(quotaError.message || 'Rate limit exceeded. Please try again later.');
                }
            } else {
                setError(error.response?.data?.message || error.message || 'Failed to generate recipe');
            }
        } finally {
            setIsGenerating(false);
        }
    };


    // Function to handle saving the AI-generated recipe
    const handleSaveAIRecipe = async () => {
        try {
            setIsSubmitting(true);
            setError('');
            
            // Use the existing handleSubmit function to save the recipe
            await handleSubmit();
            
        } catch (error) {
            console.error('Error saving AI recipe:', error);
            setError('Failed to save the recipe. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Function to edit the AI-generated recipe
    const handleEditAIRecipe = () => {
        // Switch to manual mode but keep the generated recipe data
        setIsUsingAI(false);
        setRecipeGenerated(false);
        
        // Keep the flag that this was originally AI-generated
        // setWasOriginallyAIGenerated remains true
        
        // Scroll to the top of the form
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // Function to generate a new recipe (rard current one)
    const handleGenerateNewRecipe = () => {
        // Reset the AI generation state
        setRecipeGenerated(false);
        setRecipePrompt('');
        
        // Reset the flag since this is a new recipe
        setWasOriginallyAIGenerated(false);
        
        // Clear the saved draft
        localStorage.removeItem(AI_RECIPE_DRAFT_KEY);
        
        // Reset the recipe state to defaults
        setRecipe({
            name: '',
            ingredients: [{ name: '', amount: '', unit: 'g' }],
            instructions: [{ text: '', imageUrl: '' }],
            imageUrl: '',
            cookingTime: 30,
            servings: 4,
            selection: 'quick and easy', // Add required selection field
            isVegan: false,
            isVegetarian: false,
            categories: []
        });
        
        // Clear instruction images
        setInstructionImages({});
        setInstructionImagePreviews({});
        
        // Clear ingredients photo
        setSelectedIngredientsPhoto(null);
        setIngredientsPhotoPreview(null);
        
        // Scroll to the top
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };



    // Function to upload instruction images before saving the recipe
    const uploadInstructionImages = async () => {
        const uploadPromises = [];
        
        Object.keys(instructionImages).forEach(index => {
            if (instructionImages[index]) {
                const formData = new FormData();
                formData.append('instructionImage', instructionImages[index]);
                
                const uploadPromise = axiosInstance.post(
                    '/recipes/upload-instruction-image',
                    formData,
                    {
                        headers: {
                            'Content-Type': 'multipart/form-data'
                        }
                    }
                ).then(response => {
                    // Update the instruction with the uploaded image URL
                    const instructionIndex = parseInt(index);
                    setRecipe(prev => ({
                        ...prev,
                        instructions: prev.instructions.map((instruction, i) => 
                            i === instructionIndex 
                                ? { ...instruction, imageUrl: response.data.imageUrl }
                                : instruction
                        )
                    }));
                    return { index: instructionIndex, imageUrl: response.data.imageUrl };
                }).catch(error => {
                    console.error(`Failed to upload instruction image ${index}:`, error);
                    throw error;
                });
                
                uploadPromises.push(uploadPromise);
            }
        });
        
        if (uploadPromises.length > 0) {
            await Promise.all(uploadPromises);
        }
    };

    const handleSubmit = async (event) => {
        if (event) {
            event.preventDefault();
        }
        
        if (isSubmitting) {
            console.log('Already submitting, please wait...');
            return;
        }
        
        setError('');
        setIsSubmitting(true);
        
        try {
            // First, upload any instruction images
            if (Object.keys(instructionImages).length > 0) {
                console.log('Uploading instruction images...');
                await uploadInstructionImages();
                console.log('Instruction images uploaded successfully');
            }
            // Check for authentication token from both sources
            const cookieToken = cookies.access_token;
            const localStorageToken = localStorage.getItem('auth_token');
            const hasToken = !!cookieToken || !!localStorageToken;
            
            if (!hasToken) {
                console.error('No authentication token found');
                setError('You need to be logged in to save recipes. Please log in and try again.');
                setIsSubmitting(false);
                return;
            }
            
            console.log('Recipe data before validation:', recipe);
            validateRecipe(recipe);
            
            const formData = new FormData();
            
            // Add all recipe data
            formData.append('name', recipe.name);
            formData.append('cookingTime', recipe.cookingTime.toString());
            formData.append('servings', recipe.servings.toString());
            formData.append('selection', recipe.selection || 'quick and easy'); // Add required selection field
            
            // Clean ingredients - ensure amount is a number and remove unnecessary fields
            const cleanedIngredients = recipe.ingredients.map(ingredient => ({
                name: ingredient.name,
                amount: parseFloat(ingredient.amount) || 0,
                unit: ingredient.unit,
                ...(ingredient.unit === 'other' && ingredient.customUnit ? { customUnit: ingredient.customUnit } : {})
            }));
            
            formData.append('ingredients', JSON.stringify(cleanedIngredients));
            formData.append('instructions', JSON.stringify(recipe.instructions));
            formData.append('userOwner', userID);

            // Note: Instruction images are handled separately via upload-instruction-image endpoint
            // They are already stored in recipe.instructions with their imageUrl values

            // Handle ingredients photo
            if (selectedIngredientsPhoto) {
                formData.append('ingredientsPhoto', selectedIngredientsPhoto);
                console.log('Appending ingredients photo to formData:', selectedIngredientsPhoto.name);
            } else if (recipe.ingredientsPhotoUrl && recipe.ingredientsPhotoUrl.trim()) {
                formData.append('ingredientsPhotoUrl', recipe.ingredientsPhotoUrl);
                console.log('Appending ingredients photo URL to formData:', recipe.ingredientsPhotoUrl);
            }
            formData.append('isVegan', recipe.isVegan.toString());
            formData.append('isVegetarian', recipe.isVegetarian.toString());
            formData.append('categories', JSON.stringify(recipe.categories));

            // Handle image upload with improved logging
            console.log('Processing image for upload:');
            console.log('- selectedImage:', selectedImage ? 'Present' : 'Not present');
            console.log('- imageUrl from recipe:', recipe.imageUrl);
            console.log('- generatedImageUrl:', generatedImageUrl);
            
            if (selectedImage) {
                formData.append('image', selectedImage);
                console.log('Appending file image to formData:', selectedImage.name);
            } else if (recipe.imageUrl && recipe.imageUrl.trim() && !recipe.imageUrl.includes('placeholder')) {
                // Only use non-placeholder, non-empty URLs
                formData.append('imageUrl', recipe.imageUrl);
                console.log('Appending image URL to formData:', recipe.imageUrl);
            } else {
                console.log('No image provided - recipe will be saved without an image');
            }

            // Debug log the FormData contents
            for (let pair of formData.entries()) {
                console.log(`${pair[0]}: ${pair[1] instanceof File ? pair[1].name : pair[1]}`);
            }

            const response = await axiosInstance.post(
                `/recipes`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    },
                    onUploadProgress: (progressEvent) => {
                        console.log('Upload progress:', Math.round((progressEvent.loaded * 100) / progressEvent.total));
                    }
                }
            );

            if (response.status === 201) {
                console.log('Recipe created successfully:', response.data);
                
                // Clear any saved draft after successful submission
                localStorage.removeItem(AI_RECIPE_DRAFT_KEY);
                
                // If this is a manual recipe (not originally AI-generated) and photo generation is available, show the photo generation option
                if (!isUsingAI && !wasOriginallyAIGenerated && photoGenerationAvailable) {
                    setLastCreatedRecipeId(response.data._id);
                    setShowPhotoGenerationOption(true);
                    return; // Don't navigate yet, give user option to generate photos
                }
                
                
                navigate(`/recipe/${response.data._id}`);
            } else {
                throw new Error(response.data.message || 'Failed to create recipe');
            }
        } catch (err) {
            console.error('Error submitting recipe:', {
                message: err.message,
                response: err.response?.data,
                status: err.response?.status,
                details: err.response?.data?.details,
                stack: err.stack
            });
            
            // Handle authentication error specifically
            if (err.response && err.response.status === 401) {
                setError('Authentication error. Please log out and log back in to continue.');
            } else {
                setError(
                    err.response?.data?.message || 
                    err.message || 
                    'Error creating recipe. Please try again.'
                );
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="create-recipe">
            <h2>Create New Recipe</h2>
            {error && tokenValidationAttempted && <div className="error">{error}</div>}
            
            {/* Add Recipe Recovery Banner */}
            {hasRecoveredRecipe && (
                <div className="recipe-recovery-banner">
                    <p>You have an unsaved recipe from your previous session.</p>
                    <button 
                        className="recovery-btn" 
                        onClick={recoverSavedRecipe}
                    >
                        Recover Saved Recipe
                    </button>
                    <button 
                        className="discard-btn" 
                        onClick={() => {
                            localStorage.removeItem(AI_RECIPE_DRAFT_KEY);
                            setHasRecoveredRecipe(false);
                        }}
                    >
                        Discard
                    </button>
                </div>
            )}
            
            <div className="creation-mode-toggle">
                <button 
                    className={`toggle-btn large ${!isUsingAI ? 'active' : ''}`}
                    onClick={() => setIsUsingAI(false)}
                >
                    Create Manually
                </button>
                <button 
                    className={`toggle-btn large ${isUsingAI ? 'active' : ''}`}
                    onClick={() => setIsUsingAI(true)}
                >
                    Generate with AI
                </button>
            </div>
            
            {/* AI Quota Display */}
            {isUsingAI && aiQuota && generateImage && (
                <div className="ai-quota-display">
                    <div className="quota-header">
                        <div className="quota-info">
                            <span className="quota-label">Daily AI Recipe Limit (with images):</span>
                            {aiQuota.isPowerUser ? (
                                <span className="quota-unlimited">✨ Unlimited (Power User)</span>
                            ) : (
                                <span className="quota-limited">
                                    {aiQuota.used} / {aiQuota.dailyLimit} used today
                                    {aiQuota.remaining === 0 && (
                                        <span className="quota-exhausted"> - Limit reached for today</span>
                                    )}
                                </span>
                            )}
                        </div>
                        <button 
                            className="quota-refresh-btn"
                            onClick={fetchAiQuota}
                            disabled={quotaLoading}
                            title="Refresh quota status"
                        >
                            {quotaLoading ? '🔄' : '🔄'}
                        </button>
                    </div>
                    {!aiQuota.isPowerUser && aiQuota.remaining > 0 && (
                        <div className="quota-remaining">
                            <span className="remaining-text">Remaining: {aiQuota.remaining}</span>
                        </div>
                    )}
                    {!aiQuota.isPowerUser && aiQuota.resetTime && (
                        <div className="quota-reset">
                            <span className="reset-text">
                                Resets: {aiQuota.nextResetDate || new Date(aiQuota.resetTime).toLocaleDateString()}
                                {aiQuota.timeUntilResetText && (
                                    <span className="time-until-reset"> ({aiQuota.timeUntilResetText})</span>
                                )}
                            </span>
                        </div>
                    )}
                </div>
            )}
            
            {/* AI Image Quota Display */}
            {isUsingAI && aiImageQuota && generateImage && (
                <div className="ai-quota-display" style={{ marginTop: '15px', borderColor: '#ff9800' }}>
                    <div className="quota-header">
                        <div className="quota-info">
                            <span className="quota-label">📸 Monthly AI Image Limit:</span>
                            {aiImageQuota.isPowerUser ? (
                                <span className="quota-unlimited">✨ Unlimited (Power User)</span>
                            ) : (
                                <span className="quota-limited">
                                    {aiImageQuota.used} / {aiImageQuota.monthlyLimit} used this month
                                    {aiImageQuota.remaining === 0 && (
                                        <span className="quota-exhausted"> - Limit reached for this month</span>
                                    )}
                                </span>
                            )}
                        </div>
                        <button 
                            className="quota-refresh-btn"
                            onClick={fetchAiImageQuota}
                            disabled={imageQuotaLoading}
                            title="Refresh image quota status"
                        >
                            {imageQuotaLoading ? '🔄' : '🔄'}
                        </button>
                    </div>
                    {!aiImageQuota.isPowerUser && aiImageQuota.remaining > 0 && (
                        <div className="quota-remaining">
                            <span className="remaining-text">Remaining: {aiImageQuota.remaining}</span>
                        </div>
                    )}
                    {!aiImageQuota.isPowerUser && aiImageQuota.resetTime && (
                        <div className="quota-reset">
                            <span className="reset-text">
                                Resets: {aiImageQuota.nextResetDate || new Date(aiImageQuota.resetTime).toLocaleDateString()}
                                {aiImageQuota.timeUntilResetText && (
                                    <span className="time-until-reset"> ({aiImageQuota.timeUntilResetText})</span>
                                )}
                            </span>
                        </div>
                    )}
                    {!aiImageQuota.isPowerUser && aiImageQuota.currentMonth && (
                        <div className="quota-reset">
                            <span className="reset-text">
                                Current month: {aiImageQuota.currentMonth}
                            </span>
                        </div>
                    )}
                </div>
            )}
            
            {/* Unlimited Recipe Creation Notice */}
            {isUsingAI && !generateImage && (
                <div style={{ 
                    marginTop: '15px', 
                    padding: '15px', 
                    backgroundColor: '#e8f5e8', 
                    borderRadius: '8px',
                    border: '2px solid #4caf50'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ fontSize: '24px' }}>🎉</span>
                        <div>
                            <h4 style={{ margin: '0 0 5px 0', color: '#2e7d32' }}>Daily Limit: 2 Recipes Per Day</h4>
                            <p style={{ margin: 0, color: '#388e3c', fontSize: '14px' }}>
                                Since you're not generating AI images, you can create 2 AI recipes per day. 
                                Monthly limits (1 per month) only apply when AI image generation is enabled.
                            </p>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Loading overlay */}
            {isGenerating && (
                <div className="recipe-loading-overlay">
                    <div className="recipe-loading-content">
                        <div className="recipe-loading-spinner">
                            <div className="spinner"></div>
                        </div>
                        <h3>Generating Your Recipe</h3>
                        <p className="recipe-loading-pun">{currentPun}</p>
                        
                        {/* Show current process step */}
                        {currentProcessStep && (
                            <div className="process-step-indicator">
                                <h4>Current Step:</h4>
                                <p className="current-step">{currentProcessStep}</p>
                            </div>
                        )}
                        
                        {/* Show 3-phase production process */}
                        {aiProcessDetails && (
                            <div className="process-details-live">
                                <div className="production-phases">
                                    <div className="phase-header">🏭 Production-Grade 3-Phase Architecture</div>
                                    
                                    
                                    
                                    {/* Phase 2: Deterministic Matching */}
                                    {(aiProcessDetails.phase === 2 || aiProcessDetails.ingredient_matching) && (
                                        <div className="process-phase phase-2">
                                            <div className="phase-title">⚡ Phase 2: Deterministic Ingredient Matching</div>
                                            <div className="phase-description">Fast, accurate matching to ingredient database</div>
                                            <div className="process-step completed">
                                                 Ingredients matched to database
                                            </div>
                                        </div>
                                    )}
                                    
                                    {/* Phase 4: Gemini Instructional Roadmap */}
                                    {(aiProcessDetails.phase === 4 || aiProcessDetails.gemini_roadmap_generation) && (
                                        <div className="process-phase phase-4">
                                            <div className="phase-title">🎬 Phase 4: Gemini Visual Instructions</div>
                                            <div className="phase-description">Creating step-by-step visual cooking guide with Gemini</div>
                                            <div className="process-step completed">
                                                ✅ Generating recipe image + instruction visuals
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                        
                        <p className="recipe-loading-info">This might take a minute. Our AI is working hard to create something delicious!</p>
                    </div>
                </div>
            )}
            
            {isUsingAI ? (
                <div className="ai-recipe-generator">
                    <div className="ai-daily-limit-note">
                        <p><strong>📝 Important:</strong> 
                        <br/>• <strong>With AI images:</strong> Limited to 1 recipe per month
                        <br/>• <strong>Without AI images:</strong> 2 recipes per day
                        <br/>• <strong>Manual creation:</strong> Unlimited!</p>
                    </div>
                    <h3>Generate Recipe with AI</h3>
                    
                    {!recipeGenerated ? (
                        <form onSubmit={handleGenerateRecipe}>
                            {/* Cupboard toggle if items are available */}
                            {cupboardItems.length > 0 && (
                                <div className="form-group cupboard-toggle">
                                    <label className="checkbox-label">
                                        <input
                                            type="checkbox"
                                            checked={useCupboardMode}
                                            onChange={(e) => {
                                                setUseCupboardMode(e.target.checked);
                                                if (!e.target.checked) {
                                                    setSelectedCupboardItems([]);
                                                    setCupboardMealType('');
                                                }
                                            }}
                                        />
                                        Use ingredients from my cupboard
                                    </label>
                                    <small className="form-text">Create a recipe using ingredients you already have</small>
                                </div>
                            )}
                            
                            {useCupboardMode ? (
                                <div className="cupboard-recipe-form">
                                    <div className="cupboard-form-header">
                                        <h4>Create Recipe from Cupboard Ingredients</h4>
                                    </div>
                                    
                                    {cupboardItems.length > 0 && (
                                        <div className="form-section">
                                            <label>Select ingredients to use:</label>
                                            <div className="cupboard-ingredients-selection">
                                                {cupboardItems.map(item => (
                                                    <label 
                                                        key={item._id} 
                                                        className={`cupboard-ingredient-checkbox ${selectedCupboardItems.includes(item._id) ? 'selected' : ''}`}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedCupboardItems.includes(item._id)}
                                                            onChange={(e) => {
                                                                if (e.target.checked) {
                                                                    setSelectedCupboardItems([...selectedCupboardItems, item._id]);
                                                                } else {
                                                                    setSelectedCupboardItems(selectedCupboardItems.filter(id => id !== item._id));
                                                                }
                                                            }}
                                                        />
                                                        <div className="cupboard-ingredient-info">
                                                            <span className="ingredient-name">{item.ingredientName}</span>
                                                            <span className="ingredient-amount">{item.amount} {item.unit}</span>
                                                        </div>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    
                                    <div className="form-group">
                                        <label htmlFor="cupboardMealType">What type of meal would you like? (optional)</label>
                                        <input
                                            type="text"
                                            id="cupboardMealType"
                                            value={cupboardMealType}
                                            onChange={(e) => setCupboardMealType(e.target.value)}
                                            placeholder="e.g., warm soup, hearty stew, fresh salad, dessert, etc."
                                        />
                                    </div>
                                    
                                    <div className="generated-prompt-preview">
                                        <label>Generated prompt preview:</label>
                                        <div className="prompt-preview">
                                            {selectedCupboardItems.length > 0 ? (
                                                <>
                                                    I have <strong>{cupboardItems
                                                        .filter(item => selectedCupboardItems.includes(item._id))
                                                        .map(item => item.ingredientName)
                                                        .join(', ')}</strong> in my cupboard. Please search the web for nice recipes that use these ingredients
                                                    {cupboardMealType ? <> for a <strong>{cupboardMealType}</strong></> : <> and create something <strong>nice and warm</strong></>}.
                                                </>
                                            ) : (
                                                <em>Select ingredients above to see the prompt</em>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="form-group">
                                    <label htmlFor="recipePrompt">Describe the recipe you want to generate</label>
                                    <textarea
                                        id="recipePrompt"
                                        name="recipePrompt"
                                        value={recipePrompt}
                                        onChange={(e) => setRecipePrompt(e.target.value)}
                                        placeholder="E.g., A traditional South African bobotie with yellow rice"
                                        rows={4}
                                        required={!useCupboardMode}
                                    />
                                </div>
                            )}
                            
                            <div className="form-group">
                                <label htmlFor="servingSize">Serving Size</label>
                                <input
                                    type="number"
                                    id="servingSize"
                                    min="1"
                                    max="12"
                                    value={servingSize}
                                    onChange={(e) => setServingSize(parseInt(e.target.value))}
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="preference">Recipe Preference</label>
                                <select
                                    id="preference"
                                    value={preference}
                                    onChange={(e) => setPreference(e.target.value)}
                                >
                                    <option value="cost_effective">Cost-Effective (Budget-friendly ingredients)</option>
                                    <option value="quick_and_easy">Quick & Easy (Simple, fast preparation)</option>
                                </select>
                                <small className="form-text">Choose your cooking style preference</small>
                            </div>

                            <div className="form-group">
                                <label htmlFor="ingredientComplexity">Recipe Complexity</label>
                                <select
                                    id="ingredientComplexity"
                                    value={ingredientComplexity}
                                    onChange={(e) => setIngredientComplexity(e.target.value)}
                                >
                                    <option value="simple">Simple Recipe (4-8 ingredients)</option>
                                    <option value="normal">Normal Recipe (8-16 ingredients)</option>
                                    <option value="complex">Complex Recipe (16+ ingredients, specialty items)</option>
                                </select>
                                <small className="form-text">Simple recipes are great for quick meals or beginners</small>
                            </div>
                            
                            <div className="form-section">
                                <h4 className="form-section-title">Dietary Preferences</h4>
                                <div className="dietary-preferences">
                                    <label className="checkbox-label">
                                        <input
                                            type="checkbox"
                                            name="isVegan"
                                            checked={aiDietary.isVegan}
                                            onChange={() => handleAiDietaryChange('isVegan')}
                                        />
                                        Vegan
                                    </label>
                                    <label className="checkbox-label">
                                        <input
                                            type="checkbox"
                                            name="isVegetarian"
                                            checked={aiDietary.isVegetarian}
                                            onChange={() => handleAiDietaryChange('isVegetarian')}
                                        />
                                        Vegetarian
                                    </label>
                                </div>
                            </div>

                            <div className="form-section">
                                <h4 className="form-section-title">Recipe Categories</h4>
                                <p className="category-help-text">Select all that apply</p>
                                <div className="categories-grid">
                                    {AVAILABLE_CATEGORIES.map(category => {
                                        const displayName = categoryDisplayNames[category] || 
                                            category.charAt(0).toUpperCase() + category.slice(1);
                                        return (
                                            <label 
                                                key={category} 
                                                className={`category-checkbox ${aiCategories.includes(category) ? 'selected' : ''}`}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={aiCategories.includes(category)}
                                                    onChange={() => handleAiCategoryChange(category)}
                                                />
                                                {displayName}
                                            </label>
                                        );
                                    })}
                                </div>
                            </div>


                            
                            <div className="form-group">
                                <label 
                                    className={`ai-image-checkbox ${generateImage ? 'checked' : ''}`}
                                    onClick={(e) => {
                                        // Prevent double-triggering if clicking directly on the checkbox
                                        if (e.target.type !== 'checkbox') {
                                            const checkbox = e.currentTarget.querySelector('input[type="checkbox"]');
                                            if (checkbox && !checkbox.disabled) {
                                                checkbox.click();
                                            }
                                        }
                                    }}
                                >
                                    <input
                                        type="checkbox"
                                        name="generateImage"
                                        checked={generateImage}
                                        disabled={aiImageQuota && !aiImageQuota.isPowerUser && aiImageQuota.remaining <= 0}
                                        onChange={(e) => setGenerateImage(e.target.checked)}
                                    />
                                    <span className="checkbox-text">
                                        Generate AI image for this recipe
                                        {aiImageQuota && !aiImageQuota.isPowerUser && aiImageQuota.remaining <= 0 && (
                                            <span className="limit-notice">
                                                (Monthly limit reached)
                                            </span>
                                        )}
                                    </span>
                                </label>
                                {generateImage && aiImageQuota && !aiImageQuota.isPowerUser && aiImageQuota.remaining <= 0 && (
                                    <div style={{ 
                                        color: '#d32f2f', 
                                        fontSize: '14px', 
                                        marginTop: '5px',
                                        padding: '8px',
                                        backgroundColor: '#ffebee',
                                        borderRadius: '4px',
                                        border: '1px solid #ffcdd2'
                                    }}>
                                        ⚠️ You've reached your monthly AI image generation limit. 
                                        Your quota resets on {aiImageQuota.nextResetDate}. 
                                        You can still create recipes without AI images or upload your own images.
                                    </div>
                                )}
                            </div>
                            
                            
                            
                            <button 
                                type="submit" 
                                className="generate-recipe-btn" 
                                disabled={isGenerating}
                            >
                                {isGenerating ? 'Generating...' : 'Generate Recipe'}
                            </button>
                        </form>
                    ) : (
                        <div className="recipe-generated-section">
                            <div className="generated-recipe-actions">
                                <button 
                                    onClick={() => setRecipeGenerated(false)} 
                                    className="back-to-form-btn"
                                >
                                    Back to Recipe Form
                                </button>
                            </div>
                            
                            {generateImage ? (
                                generatedImageUrl ? (
                                    <div className="generated-image">
                                        <h4> AI Generated Recipe Image</h4>
                                        <RecipeImage 
                                            imageUrl={generatedImageUrl}
                                            alt="AI Generated Recipe"
                                            style={{ 
                                                maxWidth: '300px',
                                                borderRadius: '8px',
                                                marginBottom: '20px'
                                            }}
                                        />
                                    </div>
                                ) : (
                                    <div className="generated-image-placeholder">
                                        <h4> AI Image Generation Failed</h4>
                                        <p>Image generation was requested but failed to complete</p>
                                        {error && error.includes('Image generation failed') && (
                                            <p className="image-error-details">
                                                <small>{error}</small>
                                            </p>
                                        )}
                                    </div>
                                )
                            ) : (
                                <div className="generated-image-disabled">
                                    <h4> Image Generation Disabled</h4>
                                    <p>You chose not to generate an AI image for this recipe</p>
                                </div>
                            )}
                            
                            
                            
                           
                            

                            <div className="preview-actions">
                                <button 
                                    onClick={handleEditAIRecipe}
                                    className="button-secondary edit-recipe-btn"
                                >
                                     Edit Recipe
                                </button>
                                
                                <button 
                                    onClick={handleGenerateNewRecipe}
                                    className="button-tertiary generate-new-btn"
                                >
                                     Generate New Recipe
                                </button>
                                
                                <button 
                                    onClick={handleSaveAIRecipe}
                                    className="button-primary save-recipe-btn"
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? ' Saving...' : ' Save Recipe'}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            ) : (
                <form onSubmit={handleSubmit}>
                    <div className="form-section">
                        <h3 className="form-section-title">Recipe Type</h3>
                        <div className="dietary-preferences-container">
                            <div className="dietary-preferences">
                                <label className="checkbox-label">
                                    <input
                                        type="checkbox"
                                        name="isVegan"
                                        checked={recipe.isVegan}
                                        onChange={handleChange}
                                    />
                                    Vegan
                                </label>
                                <label className="checkbox-label">
                                    <input
                                        type="checkbox"
                                        name="isVegetarian"
                                        checked={recipe.isVegetarian}
                                        onChange={handleChange}
                                    />
                                    Vegetarian
                                </label>
                            </div>
                        </div>
                    </div>

                    <div className="form-section">
                        <h3 className="form-section-title">Categories</h3>
                        <div className="categories-container">
                            <div className="categories-grid">
                                {AVAILABLE_CATEGORIES.map(category => {
                                    const displayName = categoryDisplayNames[category] || 
                                        category.charAt(0).toUpperCase() + category.slice(1);
                                    return (
                                        <label 
                                            key={category} 
                                            className={`category-checkbox ${recipe.categories.includes(category) ? 'selected' : ''}`}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={recipe.categories.includes(category)}
                                                onChange={() => handleCategoryChange(category)}
                                            />
                                            {displayName}
                                        </label>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    <div className="form-section">
                        <h3 className="form-section-title">Ingredients</h3>
                        <div className="form-group">
                            {recipe.ingredients.map((ingredient, index) => (
                                <RecipeIngredientInput
                                    key={index}
                                    ingredient={ingredient}
                                    index={index}
                                    handleIngredientChange={handleIngredientChange}
                                    removeIngredient={handleRemoveIngredient}
                                />
                            ))}
                            <button 
                                type="button" 
                                onClick={handleAddIngredient}
                                style={{
                                    backgroundColor: '#000000',
                                    color: 'white',
                                    border: 'none',
                                    padding: '8px 16px',
                                    borderRadius: '4px',
                                    marginTop: '10px'
                                }}
                            >
                                Add Ingredient
                            </button>
                        </div>
                        
                        {/* Ingredients Photo Upload */}
                        <div className="form-group">
                            <label>Ingredients Photo (Optional)</label>
                            <p style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
                                Upload a photo showing all the ingredients needed for this recipe
                            </p>
                            <div className="image-upload-container">
                                {ingredientsPhotoPreview && (
                                    <div className="image-preview">
                                        <img 
                                            src={ingredientsPhotoPreview}
                                            alt="Ingredients preview"
                                            style={{ 
                                                maxWidth: '200px',
                                                maxHeight: '200px',
                                                objectFit: 'cover',
                                                marginBottom: '10px',
                                                borderRadius: '8px',
                                                border: '1px solid #ddd'
                                            }}
                                        />
                                        <button 
                                            type="button"
                                            onClick={removeIngredientsPhoto}
                                            style={{
                                                marginLeft: '10px',
                                                padding: '4px 8px',
                                                backgroundColor: '#dc3545',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '4px',
                                                fontSize: '12px'
                                            }}
                                        >
                                            Remove Photo
                                        </button>
                                    </div>
                                )}
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleIngredientsPhotoChange}
                                    className="image-upload-input"
                                />
                                <p className="or-divider">OR</p>
                                <input
                                    type="text"
                                    name="ingredientsPhotoUrl"
                                    value={recipe.ingredientsPhotoUrl}
                                    onChange={handleChange}
                                    placeholder="Enter ingredients photo URL (optional)"
                                    className="image-url-input"
                                />
                                <small style={{ color: '#666', marginTop: '5px' }}>
                                    You can skip adding an ingredients photo if you don't have one available.
                                </small>
                            </div>
                        </div>
                    </div>

                    <div className="form-section">
                        <h3 className="form-section-title">Instructions</h3>
                        <div className="form-group">
                            {recipe.instructions.map((instruction, index) => (
                                <div key={index} className="instruction-input-container" style={{ marginBottom: '20px', padding: '15px', border: '1px solid #ddd', borderRadius: '8px' }}>
                                    <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                                        <span style={{ fontWeight: 'bold', minWidth: '30px' }}>{index + 1}.</span>
                                        <textarea
                                            value={instruction.text || ''}
                                            onChange={(e) => handleInstructionChange(index, 'text', e.target.value)}
                                            placeholder={`Step ${index + 1} instructions...`}
                                            style={{ flex: 1, minHeight: '60px' }}
                                        />
                                        {recipe.instructions.length > 1 && (
                                            <button 
                                                type="button" 
                                                onClick={() => removeInstruction(index)}
                                                className="button-danger"
                                                style={{ padding: '4px 8px', height: 'fit-content' }}
                                            >
                                                Remove
                                            </button>
                                        )}
                                    </div>
                                    
                                    {/* Instruction Image Upload */}
                                    <div className="instruction-image-section" style={{ marginTop: '10px' }}>
                                        <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500' }}>
                                            Step Image (Optional):
                                        </label>
                                        
                                        {(instructionImagePreviews[index] || (instruction.imageUrl && instruction.imageUrl !== '')) && (
                                            <div style={{ marginBottom: '10px' }}>
                                                <img 
                                                    src={instructionImagePreviews[index] || instruction.imageUrl} 
                                                    alt={`Step ${index + 1}`}
                                                    style={{ 
                                                        maxWidth: '200px', 
                                                        maxHeight: '150px', 
                                                        objectFit: 'cover',
                                                        borderRadius: '4px',
                                                        border: '1px solid #ddd'
                                                    }}
                                                />
                                                <button 
                                                    type="button"
                                                    onClick={() => removeInstructionImage(index)}
                                                    style={{
                                                        marginLeft: '10px',
                                                        padding: '4px 8px',
                                                        backgroundColor: '#dc3545',
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '4px',
                                                        fontSize: '12px'
                                                    }}
                                                >
                                                    Remove Image
                                                </button>
                                            </div>
                                        )}
                                        
                                        {/* Image URL Input */}
                                        <div style={{ marginBottom: '10px' }}>
                                            <label style={{ display: 'block', marginBottom: '3px', fontSize: '12px', color: '#666' }}>
                                                Image URL:
                                            </label>
                                            <input
                                                type="url"
                                                placeholder="https://example.com/image.jpg"
                                                value={instruction.imageUrl || ''}
                                                onChange={(e) => handleInstructionChange(index, 'imageUrl', e.target.value)}
                                                style={{ 
                                                    width: '100%', 
                                                    padding: '8px', 
                                                    border: '1px solid #ddd', 
                                                    borderRadius: '4px',
                                                    fontSize: '14px'
                                                }}
                                            />
                                        </div>
                                        
                                        {/* File Upload */}
                                        <div style={{ marginBottom: '10px' }}>
                                            <label style={{ display: 'block', marginBottom: '3px', fontSize: '12px', color: '#666' }}>
                                                Or upload file:
                                            </label>
                                            <input
                                                type="file"
                                                accept="image/*"
                                                onChange={(e) => handleInstructionImageChange(index, e)}
                                                style={{ fontSize: '14px' }}
                                            />
                                        </div>
                                        
                                        <small style={{ display: 'block', marginTop: '5px', color: '#666' }}>
                                            Add an image URL or upload a file to help illustrate this step
                                        </small>
                                    </div>
                                </div>
                            ))}
                            <button 
                                type="button" 
                                onClick={addInstruction}
                                className="button-secondary"
                                style={{ marginTop: '10px' }}
                            >
                                Add Step
                            </button>
                        </div>
                    </div>

                    <div className="form-section">
                        <h3 className="form-section-title">Recipe Details</h3>
                        <div className="form-group">
                            <label htmlFor="name">Name</label>
                            <input
                                type="text"
                                id="name"
                                name="name"
                                value={recipe.name}
                                onChange={handleChange}
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label>Recipe Image (Optional)</label>
                            <div className="image-upload-container">
                                {imagePreview && (
                                    <div className="image-preview">
                                        <RecipeImage 
                                            imageUrl={imagePreview}
                                            alt="Recipe preview"
                                            style={{ 
                                                maxWidth: '200px',
                                                maxHeight: '200px',
                                                objectFit: 'cover',
                                                marginBottom: '10px'
                                            }}
                                        />
                                    </div>
                                )}
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleImageChange}
                                    className="image-upload-input"
                                />
                                <p className="or-divider">OR</p>
                                <input
                                    type="text"
                                    name="imageUrl"
                                    value={recipe.imageUrl}
                                    onChange={handleChange}
                                    placeholder="Enter image URL (optional)"
                                    className="image-url-input"
                                />
                                <small style={{ color: '#666', marginTop: '5px' }}>
                                    You can skip adding an image if you don't have one available.
                                </small>
                            </div>
                        </div>

                        <div className="form-group">
                            <label htmlFor="cookingTime">Cooking Time (minutes)</label>
                            <input
                                type="number"
                                id="cookingTime"
                                name="cookingTime"
                                value={recipe.cookingTime}
                                onChange={handleChange}
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="servings">Servings</label>
                            <input
                                type="number"
                                id="servings"
                                name="servings"
                                min="1"
                                value={recipe.servings}
                                onChange={handleChange}
                                required
                            />
                        </div>
                    </div>

                    {/* Manual Recipe Creation Notice */}
                    <div style={{ 
                        marginBottom: '20px', 
                        padding: '15px', 
                        backgroundColor: '#e3f2fd', 
                        borderRadius: '8px',
                        border: '2px solid #2196f3'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <span style={{ fontSize: '24px' }}>✍️</span>
                            <div>
                                <h4 style={{ margin: '0 0 5px 0', color: '#1976d2' }}>Manual Recipe Creation</h4>
                                <p style={{ margin: 0, color: '#1565c0', fontSize: '14px' }}>
                                    Create unlimited recipes manually! No daily limits apply to manual recipe creation.
                                    You can always upload your own images or generate AI photos later (subject to monthly limits).
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Photo Generation Section for Manual Recipes */}
                    {photoGenerationAvailable && (
                        <div className="photo-generation-section" style={{ 
                            marginBottom: '30px', 
                            padding: '20px', 
                            border: '2px solid #e0e0e0', 
                            borderRadius: '8px',
                            backgroundColor: '#f9f9f9'
                        }}>
                            <h3 style={{ marginTop: 0, color: '#333' }}>🎨 AI Photo Generation</h3>
                            <p style={{ color: '#666', marginBottom: '15px' }}>
                                After creating your recipe, you can generate professional photos including ingredient overview and step-by-step cooking photos.
                                <br/><strong>⚠️ Important:</strong> AI photo generation is limited to once per month.
                            </p>
                            
                            {photoGenerationError && (
                                <div style={{ 
                                    color: '#d32f2f', 
                                    backgroundColor: '#ffebee', 
                                    padding: '10px', 
                                    borderRadius: '4px', 
                                    marginBottom: '15px',
                                    border: '1px solid #ffcdd2'
                                }}>
                                    ❌ {photoGenerationError}
                                </div>
                            )}
                            
                            {photoGenerationSuccess && (
                                <div style={{ 
                                    color: '#2e7d32', 
                                    backgroundColor: '#e8f5e8', 
                                    padding: '10px', 
                                    borderRadius: '4px', 
                                    marginBottom: '15px',
                                    border: '1px solid #c8e6c9'
                                }}>
                                    ✅ {photoGenerationSuccess}
                                </div>
                            )}
                            
                            <div style={{ fontSize: '14px', color: '#666' }}>
                                💡 <strong>Tip:</strong> Create your recipe first, then you'll be prompted to generate photos automatically!
                            </div>
                        </div>
                    )}

                    <button 
                        type="submit" 
                        className="update-recipe-btn" 
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? 'Creating Recipe...' : 'Create Recipe'}
                    </button>
                </form>
            )}

            {/* Photo Generation Option for Manual Recipes */}
            {showPhotoGenerationOption && lastCreatedRecipeId && (
                <div className="photo-generation-modal" style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        padding: '30px',
                        borderRadius: '12px',
                        maxWidth: '500px',
                        width: '90%',
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                        textAlign: 'center'
                    }}>
                        <h3 style={{ marginTop: 0, color: '#333', fontSize: '24px' }}>🎉 Recipe Created Successfully!</h3>
                        
                        {!isGeneratingPhotos && !photoGenerationSuccess && (
                            <>
                                <p style={{ color: '#666', marginBottom: '20px', lineHeight: '1.5' }}>
                                    Would you like to generate professional AI photos for your recipe? 
                                    This will add an ingredient overview photo and step-by-step cooking photos.
                                </p>
                                
                                <div style={{ display: 'flex', gap: '15px', justifyContent: 'center' }}>
                                    <button
                                        onClick={() => handleGeneratePhotosForRecipe(lastCreatedRecipeId)}
                                        style={{
                                            backgroundColor: '#4CAF50',
                                            color: 'white',
                                            border: 'none',
                                            padding: '12px 24px',
                                            borderRadius: '6px',
                                            fontSize: '16px',
                                            fontWeight: 'bold',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        📸 Generate Photos
                                    </button>
                                    
                                    <button
                                        onClick={() => {
                                            setShowPhotoGenerationOption(false);
                                            navigate(`/recipe/${lastCreatedRecipeId}`);
                                        }}
                                        style={{
                                            backgroundColor: '#f5f5f5',
                                            color: '#333',
                                            border: '1px solid #ddd',
                                            padding: '12px 24px',
                                            borderRadius: '6px',
                                            fontSize: '16px',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        Skip for Now
                                    </button>
                                </div>
                            </>
                        )}
                        
                        {isGeneratingPhotos && (
                            <div style={{ padding: '20px 0' }}>
                                <div style={{ 
                                    display: 'inline-block', 
                                    width: '32px', 
                                    height: '32px', 
                                    border: '3px solid #f3f3f3', 
                                    borderTop: '3px solid #4CAF50', 
                                    borderRadius: '50%', 
                                    animation: 'spin 1s linear infinite',
                                    marginBottom: '15px'
                                }}></div>
                                <h4 style={{ margin: '0 0 10px 0', color: '#333' }}> AI Photo Generation</h4>
                                
                                {/* Current status */}
                                {createRecipePhotoProgress && (
                                    <div style={{ 
                                        backgroundColor: '#f8f9fa', 
                                        padding: '8px 12px', 
                                        borderRadius: '4px',
                                        marginBottom: '10px',
                                        border: '1px solid #e9ecef'
                                    }}>
                                        <p style={{ margin: 0, fontSize: '13px', color: '#333', fontWeight: '500' }}>
                                            {createRecipePhotoProgress}
                                        </p>
                                    </div>
                                )}
                                
                                <p style={{ color: '#666', margin: 0, fontSize: '14px' }}>
                                    Creating professional photos for your recipe...
                                </p>
                                <p style={{ color: '#999', fontSize: '12px', margin: '5px 0 0 0' }}>
                                    This typically takes 2-3 minutes. Please wait.
                                </p>
                            </div>
                        )}
                        
                        {photoGenerationError && (
                            <>
                                <div style={{ 
                                    color: '#d32f2f', 
                                    backgroundColor: '#ffebee', 
                                    padding: '15px', 
                                    borderRadius: '6px', 
                                    marginBottom: '20px',
                                    border: '1px solid #ffcdd2',
                                    whiteSpace: 'pre-line',
                                    textAlign: 'left'
                                }}>
                                    ❌ {photoGenerationError}
                                </div>
                                
                                <div style={{ display: 'flex', gap: '15px', justifyContent: 'center' }}>
                                    <button
                                        onClick={() => handleGeneratePhotosForRecipe(lastCreatedRecipeId)}
                                        style={{
                                            backgroundColor: '#4CAF50',
                                            color: 'white',
                                            border: 'none',
                                            padding: '12px 24px',
                                            borderRadius: '6px',
                                            fontSize: '16px',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        Try Again
                                    </button>
                                    
                                    <button
                                        onClick={() => {
                                            setShowPhotoGenerationOption(false);
                                            navigate(`/recipe/${lastCreatedRecipeId}`);
                                        }}
                                        style={{
                                            backgroundColor: '#f5f5f5',
                                            color: '#333',
                                            border: '1px solid #ddd',
                                            padding: '12px 24px',
                                            borderRadius: '6px',
                                            fontSize: '16px',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        Continue Without Photos
                                    </button>
                                </div>
                            </>
                        )}
                        
                        {photoGenerationSuccess && (
                            <>
                                <div style={{ 
                                    color: '#2e7d32', 
                                    backgroundColor: '#e8f5e8', 
                                    padding: '15px', 
                                    borderRadius: '6px', 
                                    marginBottom: '20px',
                                    border: '1px solid #c8e6c9'
                                }}>
                                    ✅ {photoGenerationSuccess}
                                </div>
                                
                                <button
                                    onClick={() => {
                                        setShowPhotoGenerationOption(false);
                                        navigate(`/recipe/${lastCreatedRecipeId}`);
                                    }}
                                    style={{
                                        backgroundColor: '#4CAF50',
                                        color: 'white',
                                        border: 'none',
                                        padding: '12px 24px',
                                        borderRadius: '6px',
                                        fontSize: '16px',
                                        fontWeight: 'bold',
                                        cursor: 'pointer'
                                    }}
                                >
                                    View Recipe with Photos
                                </button>
                            </>
                        )}
                    </div>
                </div>
            )}

        </div>
    );
} 